/* eslint-disable max-lines */
import { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import H1 from '@base/H1/H1';
import H2 from '@base/H2/H2';
import H6 from '@base/H6/H6';
import P1 from '@base/P1/P1';
import Logo from '@comp/Logo/Logo';
import Paper from '@base/Paper/Paper';
import Button from '@base/Button/Button';
import { getIsLoggedIn } from '@api/selectors/auth';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import { Fonts, Spacings, mediaQuery } from '@styles/index';
import { getImage } from '@utils/imageHelper';
import {
  RANK_PREDICTOR_RANK_CLICKED,
  RANK_PREDICTOR_RANK_FAILED
} from '@constants/segment';
import { segment } from '@actions/analytics';
import GoalCmsContent from '@cont/PreSubscribed/GoalCmsContent';
import DropdownInput from '@cont/RankPredictor/Gate/Dropdown';
import P2 from '@base/P2/P2';
import fetch from '@api/fetchers';
import Play from 'aquilla/icons/actions/Play';
import { useToggleable } from '@stores/toggleables';
import { LOGIN_DRAWER } from '@constants/toggleables';
import Input from 'aquilla/core/molecules/Input';
import RankCard from './RankCard';
import GateSolutions from './GateSolutions';

const POINTS = [
  'Get estimated rank based on your response sheet',
  'Follow simple steps and get your rank',
  'Get access to complete video solutions'
];

const ExamName = 'GATE';
const currentYear = new Date().getFullYear();

const Wrapper = styled.div`
  padding-bottom: 120px;
  padding-left: 13vw;
  padding-right: 13vw;
  display: flex;
  justify-content: space-between;
  background: linear-gradient(
    180deg,
    rgba(255, 255, 255, 0) 0%,
    rgba(229, 244, 255, 0.44) 100%
  );

  ${mediaQuery.uptoTablet} {
    padding-left: 10vw;
    padding-right: 10vw;
  }

  ${mediaQuery.uptoMobile} {
    flex-direction: column;
    padding: 0px;
  }
`;

const Header = styled.div`
  height: ${Spacings.SPACING_HEADER}px;
  padding-left: 13vw;
  padding-right: 13vw;
  display: flex;

  ${mediaQuery.uptoTablet} {
    padding-left: 10vw;
    padding-right: 10vw;
  }

  ${mediaQuery.uptoMobile} {
    padding: ${Spacings.SPACING_4B};
  }
`;

const Heading = styled(H1)`
  font-size: ${Fonts.LARGE_HEADING};
  margin-top: ${Spacings.SPACING_10B};
  margin-bottom: ${Spacings.SPACING_6B};

  ${mediaQuery.uptoMobile} {
    font-size: ${Fonts.H2};
    margin-top: ${Spacings.SPACING_0B};
    margin-bottom: ${Spacings.SPACING_5B};
  }
`;

const LeftWrapper = styled.div`
  width: 45%;
  margin-top: ${Spacings.SPACING_13B};

  ${mediaQuery.uptoMobile} {
    width: 100%;
    padding: ${Spacings.SPACING_4B};
    margin-top: ${Spacings.SPACING_0B};
    padding-bottom: 0px;
  }
`;

const Point = styled(P1)`
  display: flex;
  align-items: center;
  font-size: ${Fonts.LARGE};
  margin-bottom: ${Spacings.SPACING_4B};

  span {
    margin-left: ${Spacings.SPACING_2B};
    font-size: ${Fonts.SMALL};
  }
  ${mediaQuery.uptoMobile} {
    margin-bottom: ${Spacings.SPACING_3B};
  }
`;

const MainImage = styled.img`
  height: ${Spacings.SPACING_35B};
  width: ${Spacings.SPACING_35B};

  ${mediaQuery.uptoMobile} {
    float: right;
    height: ${Spacings.SPACING_20B};
    width: ${Spacings.SPACING_20B};
  }
`;

const CheckedImage = styled.img`
  height: ${Spacings.SPACING_4B};
  width: ${Spacings.SPACING_4B};
`;

const RightWrapper = styled(Paper)`
  width: 45%;
  height: ${({ $large }) => ($large ? '1196px' : '640px')};

  ${mediaQuery.uptoTablet} {
    width: 100%;
    height: auto;
    box-shadow: none;
    padding: ${Spacings.SPACING_4B};
    padding-top: ${Spacings.SPACING_9B};
  }
`;

const Title = styled(H2)`
  margin-bottom: ${Spacings.SPACING_3B};
`;

const StyP1 = styled(P1)`
  ${mediaQuery.uptoTablet} {
    font-size: ${Fonts.ULTRA_SMALL};
  }
`;

const StyH6 = styled(H6)`
  margin-bottom: ${Spacings.SPACING_2B};
  margin-top: ${Spacings.SPACING_4B};
  ${mediaQuery.uptoTablet} {
    margin-bottom: ${Spacings.SPACING_1B};
  }
`;

const FormWrapper = styled.div`
  margin-top: ${Spacings.SPACING_10B};
  margin-bottom: ${Spacings.SPACING_10B};

  ${mediaQuery.uptoMobile} {
    margin-bottom: ${Spacings.SPACING_6B};
    margin-top: ${Spacings.SPACING_6B};
  }
  #cke_editor1 {
    border-style: solid;
    border-width: 1px;
    border-color: var(--color-divider);
    border-radius: ${Spacings.SPACING_6};
    transition: border-color 400ms ease-in-out;
  }
  #cke_editor1 #cke_1_top,
  #cke_editor1 #cke_1_bottom {
    display: none;
  }
  #cke_1_contents {
    height: 172px !important;
  }
`;

const StyledButton = styled(Button)`
  margin-top: 15px;
  ${mediaQuery.uptoMobile} {
    width: 90%;
    margin-top: ${Spacings.SPACING_1B};
  }
`;

const GateHtmlResponse = styled.div`
  height: 0px;
  width: 0px;
  overflow: hidden;
`;

const GoalCmsContentWrapper = styled.div`
  padding: ${Spacings.SPACING_15B} 13vw;
  padding-bottom: ${Spacings.SPACING_30B};
  background: var(--color-base-1);
  > :first-child {
    margin: ${Spacings.SPACING_0B};
  }

  ${mediaQuery.uptoMobile} {
    padding: ${Spacings.SPACING_9B} ${Spacings.SPACING_4B}
      ${Spacings.SPACING_4B} ${Spacings.SPACING_0B};
    background: none;
  }
`;

const StyledP2 = styled(P2)`
  font-weight: ${Fonts.SEMIBOLD};
  color: var(--color-i-red);
  margin-top: ${Spacings.SPACING_2B};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ErrorWrapper = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${Spacings.SPACING_6B};
`;

const UpperSection = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 15px;

  ${mediaQuery.uptoMobile} {
    grid-template-columns: repeat(1, 1fr);
  }
`;

const Points = styled(P2)`
  margin-bottom: 6px;
`;

const StyLink = styled.a`
  color: var(--color-primary-focus);
  font-size: 12px;
  text-decoration-line: underline;
`;

const ExtraPoint = styled.a`
  line-height: 150%;
  color: var(--color-primary-focus);
  text-decoration-line: underline;
  text-decoration-color: var(--color-i-blue);
  display: flex;
  align-items: center;
  font-size: ${Fonts.LARGE};
  margin-top: ${Spacings.SPACING_8B};

  span {
    margin-left: ${Spacings.SPACING_2B};
    font-size: ${Fonts.SMALL};
  }
  ${mediaQuery.uptoMobile} {
    margin-bottom: ${Spacings.SPACING_3B};
  }
`;

const Text = styled.span`
  color: var(--color-i-blue);
`;

const TextArea = styled(Input)`
  width: 100%;
  height: 150px;
`;

const URLInput = ({ setStep, setRankData }) => {
  const [formData, setFormData] = useState({
    answers: null,
    branch: null,
    shift: null
  });
  const isLoggedIn = useMyInfo(getIsLoggedIn);
  const [error, setError] = useState({
    answers: false,
    branch: false,
    shift: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const errorMsg = {
    branch: 'Branch is required field',
    shift: 'Slot is required field',
    answers:
      'The url entered is not valid. Please follow the steps mentioned above.'
  };

  const parseRank = async (data) => {
    const payload = { version: 2, response_url: JSON.stringify(data) };
    const rankData = await fetch('v1/users/gate_rank_predictor/', {
      requestType: 'POST',
      body: payload,
      useCamelCase: false
    });
    if (!rankData?.error) {
      return {
        ...rankData,
        branch: rankData.branch
        // response_url: url
      };
    }
    return false;
  };

  const sendFailedEvent = () => {
    segment.track(RANK_PREDICTOR_RANK_FAILED, {
      // rank_predictor_url: url,
      exam_type: ExamName,
      failed_reason: 'Incorrect URL'
    });
  };
  const handleOnSubmit = async () => {
    // setIsSubmitting(true);
    // eslint-disable-next-line no-undef
    const answersUrl = formData.answers?.trim();
    const docUrlRegex =
      /^https:\/\/cdn\.digialm\.com\/{1,2}per\/g01\/pub\/.*\.html$/;

    segment.track(RANK_PREDICTOR_RANK_CLICKED, {
      // rank_predictor_url: url,
      exam_type: ExamName
    });

    const payLoad = {
      answers_data: answersUrl,
      branch: formData.branch?.value,
      shift: formData.shift?.value
    };

    let isInvalidResponse = false;

    if (!answersUrl || !answersUrl.match(docUrlRegex)) {
      setError({ ...error, answers: true });
      isInvalidResponse = true;
    } else setError({ ...error, answers: false });

    if (!formData.branch) {
      setError({ ...error, branch: true });
      isInvalidResponse = true;
    }

    if (formData.branch?.value === 'CE' && !formData.shift) {
      setError({ ...error, shift: true });
      isInvalidResponse = true;
    }

    if (formData.branch?.value === 'CS' && !formData.shift) {
      setError({ ...error, shift: true });
      isInvalidResponse = true;
    }

    if (isInvalidResponse) {
      setIsSubmitting(false);
      sendFailedEvent();
      return;
    }

    const data = await parseRank(payLoad);

    if (!data) {
      setIsSubmitting(false);
      setError({ ...error, answers: true });
      sendFailedEvent();
      return;
    }
    setRankData(data);
    if (isLoggedIn) {
      setStep('showRank');
    } else {
      setStep('login');
    }
  };

  const branchOptions = [
    { value: 'AE', label: 'Aerospace Engineering' },
    { value: 'AG', label: 'Agricultural Engineering' },
    { value: 'AR', label: 'Architecture and Planning' },
    { value: 'BM', label: 'Biomedical Engineering' },
    { value: 'BT', label: 'Biotechnology' },
    { value: 'CH', label: 'Chemical Engineering' },
    { value: 'CY', label: 'Chemistry' },
    { value: 'CE', label: 'Civil Engineering' },
    { value: 'CS', label: 'Computer Science and Information Technology' },
    { value: 'DA', label: 'Data Science and Artificial Intelligence' },
    { value: 'EY', label: 'Ecology and Evolution' },
    { value: 'EE', label: 'Electrical Engineering' },
    { value: 'EC', label: 'Electronics and Communication Engineering' },
    { value: 'XE', label: 'Engineering Sciences' },
    { value: 'ES', label: 'Environmental Science and Engineering' },
    { value: 'GG', label: 'Geology and Geophysics' },
    { value: 'GE', label: 'Geomatics Engineering' },
    { value: 'XH', label: 'Humanities and Social Sciences' },
    { value: 'IN', label: 'Instrumentation Engineering' },
    { value: 'XL', label: 'Life Sciences' },
    { value: 'MA', label: 'Mathematics' },
    { value: 'ME', label: 'Mechanical Engineering' },
    { value: 'MT', label: 'Metallurgical Engineering' },
    { value: 'MN', label: 'Mining Engineering' },
    { value: 'NM', label: 'Naval Architecture and Marine Engineering' },
    { value: 'PE', label: 'Petroleum Engineering' },
    { value: 'PH', label: 'Physics' },
    { value: 'PI', label: 'Production and Industrial Engineering' },
    { value: 'ST', label: 'Statistics' },
    { value: 'TF', label: 'Textile Engineering and Fibre Science' }
  ];

  const shiftOptions = [
    { value: 'M', label: 'Shift 1' },
    { value: 'A', label: 'Shift 2' }
  ];

  function handleBranchChange(newBranch) {
    setFormData({ ...formData, branch: newBranch });
    setError({ ...error, branch: false });
  }

  function handleShiftChange(newShift) {
    setFormData({ ...formData, shift: newShift });
    setError({ ...error, shift: false });
  }

  function handleValueChange(eventValue) {
    setFormData({ ...formData, answers: eventValue.target.value });
    setError({ ...error, answers: false });
  }

  return (
    <>
      <Title>Enter your exam details</Title>
      <StyP1>Predict your GATE rank using the marksheet</StyP1>
      <FormWrapper>
        <UpperSection>
          <DropdownInput
            options={branchOptions}
            selectedOption={formData.branch}
            placeholder="Branch"
            // eslint-disable-next-line react/jsx-no-bind
            handleClick={handleBranchChange}
            error={error.branch}
            errorMsg={errorMsg.branch}
          />
          {formData.branch?.value === 'CE' ||
          formData.branch?.value === 'CS' ? (
            <DropdownInput
              options={shiftOptions}
              selectedOption={formData.shift}
              placeholder="Slot"
              // eslint-disable-next-line react/jsx-no-bind
              handleClick={handleShiftChange}
              error={error.shift}
              errorMsg={errorMsg.shift}
            />
          ) : null}
        </UpperSection>
        <StyH6>Paste your responses</StyH6>
        <Points>
          1. Log in to the{' '}
          <StyLink href="https://gate2026.iitg.ac.in/" newTab>
            GATE website
          </StyLink>
        </Points>
        <Points>2. Click on ‘View Responses’</Points>
        <Points>3. Copy the URL of your response sheet</Points>
        <Points>4. Paste the URL in the box below</Points>
        <TextArea
          className="result-data"
          name="r-data"
          id="r-data"
          multiLine
          value={formData.answers}
          // eslint-disable-next-line react/jsx-no-bind
          onChange={handleValueChange}
        />
        {error.answers && (
          <ErrorWrapper>
            {error.answers && <StyledP2>{errorMsg.answers}</StyledP2>}
          </ErrorWrapper>
        )}
        <StyledButton
          label={isSubmitting ? 'Submitting...' : 'Predict my rank'}
          variant={isSubmitting ? 'disabled' : 'filled'}
          onClick={handleOnSubmit}
        />
      </FormWrapper>
    </>
  );
};

const RankPredictor = () => {
  const [step, setStep] = useState('urlInput');
  const [rankData, setRankData] = useState(null);
  const isLoggedIn = useMyInfo(getIsLoggedIn);
  const { openElement: openLoginDrawer } = useToggleable(LOGIN_DRAWER);
  useEffect(() => {
    if (isLoggedIn && rankData) {
      setStep('showRank');
    } else if (step === 'login' && !isLoggedIn) {
      openLoginDrawer();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn, rankData, step]);

  return (
    <>
      <Header>
        <Logo />
      </Header>
      <Wrapper>
        <LeftWrapper>
          <MainImage
            src={getImage('rankpredictor/rank-predictor-logo.svg')}
            alt="rank-main-logo"
          />
          <Heading>GATE Rank Predictor {currentYear}</Heading>
          {POINTS.map((point, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <Point key={String(index)}>
              <CheckedImage
                src={getImage('rankpredictor/checked.svg')}
                alt="checked"
              />
              <span>{point}</span>
            </Point>
          ))}
          <ExtraPoint href="https://www.youtube.com/live/l6gtbx1WDTY" newTab>
            <Play width="8px" height="8px" color="var(--color-i-blue)" />
            <Text>Watch how to use GATE rank predictor</Text>
          </ExtraPoint>
        </LeftWrapper>
        <RightWrapper $large={step === 'showRank' && isLoggedIn}>
          {(step === 'urlInput' || step === 'login') && (
            <URLInput setStep={setStep} setRankData={setRankData} />
          )}
          {step === 'showRank' && isLoggedIn && (
            <RankCard
              setStep={setStep}
              rankData={rankData}
              setRankData={setRankData}
            />
          )}
        </RightWrapper>
      </Wrapper>
      <GateSolutions />
      <GoalCmsContentWrapper>
        <GoalCmsContent width="100%" />
      </GoalCmsContentWrapper>
      <GateHtmlResponse id="gateHtmlResponse" />
    </>
  );
};

export default RankPredictor;
